import { useState, useEffect } from "react";
import logo from "../logo/Logo-Viktoria-Mitte-3.png";

import AddTeamsForm from "./AddTeamsForm";
import AddPlayerForm from "./AddPlayerForm";
import AddGameForm from "./AddGameForm";
import AddPlayerStatsForm from "./AddPlayerStatsForm";

import PlayerStats from "../PlayerStats";
import AdvancedStats from "../AdvancedStats";

const TABS = [
  "Teams",
  "Players",
  "Games",
  "Stats (Edit)",
  "Player Stats (View)",
  "Advanced Stats (View)",
  "Logout",
];

function TabBar({ tabs, activeTab, onChange }) {
  return (
    <div className="flex flex-wrap gap-4 mb-6 border-b-2 border-gray-300 justify-center">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onChange(tab)}
          className={`px-4 py-2 font-semibold ${
            activeTab === tab
              ? "border-b-4 border-blue-800 text-blue-800"
              : "text-gray-600 hover:text-blue-700"
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

export default function AdminPage({ onLogout }) {
  const API = process.env.REACT_APP_API_URL || "";
  const accessToken = localStorage.getItem("accessToken");

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [activeTab, setActiveTab] = useState("Teams");

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await fetch(`${API}/teams`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        setTeams(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch teams:", err);
      }
    };
    fetchTeams();
  }, [API, accessToken]);

  const selectedTeamName =
    teams.find((t) => t.id === selectedTeamId)?.name || null;

  const handleTabChange = (tab) => {
    if (tab === "Logout") {
      onLogout();
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <header className="bg-blue-800 text-white py-6 shadow-md rounded-b-2xl mb-6">
        <div className="flex flex-col items-center space-y-4">
          <img src={logo} alt="Logo" className="h-20 w-auto" />
          <h1 className="text-3xl font-bold text-center">
            {selectedTeamName
              ? `${selectedTeamName} — Admin Dashboard`
              : "Admin Dashboard"}
          </h1>
        </div>
      </header>

      {/* Team Selector */}
      <div className="max-w-md mx-auto mb-6">
        <label className="block mb-2 font-semibold text-gray-700">
          Select a Team
        </label>
        <select
          value={selectedTeamId ?? ""}
          onChange={(e) => setSelectedTeamId(Number(e.target.value))}
          className="w-full border p-2 rounded"
        >
          <option value="">-- Select Team --</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      {selectedTeamId && (
        <>
          <TabBar
            tabs={TABS}
            activeTab={activeTab}
            onChange={handleTabChange}
          />

          <div className="tab-content">
            {activeTab === "Teams" && (
              <AddTeamsForm apiUrl={API} accessToken={accessToken} />
            )}

            {activeTab === "Players" && (
              <AddPlayerForm
                API={API}
                accessToken={accessToken}
                selectedTeamId={selectedTeamId}
              />
            )}

            {activeTab === "Games" && (
              <AddGameForm
                API={API}
                accessToken={accessToken}
                selectedTeamId={selectedTeamId}
              />
            )}

            {activeTab === "Stats (Edit)" && (
              <AddPlayerStatsForm
                API={API}
                accessToken={accessToken}
                selectedTeamId={selectedTeamId}
                getTeamName={(id) =>
                  teams.find((t) => t.id === id)?.name || `Team ${id}`
                }
              />
            )}

            {activeTab === "Player Stats (View)" && (
              <PlayerStats teamId={selectedTeamId} />
            )}

            {activeTab === "Advanced Stats (View)" && (
              <AdvancedStats teamId={selectedTeamId} />
            )}
          </div>
        </>
      )}

      {!selectedTeamId && (
        <p className="mt-6 text-gray-500 text-lg text-center">
          <strong>Select a team</strong> to access admin tools and stats.
        </p>
      )}
    </div>
  );
}
